import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 16px ${theme.palette.primary.main}80 !important` 
      : `0 0 12px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  dialogActions: {
    padding: theme.spacing(2, 2.5),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
}));

const FormModal = ({ open, onClose, formId }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [slug, setSlug] = useState("");

  useEffect(() => {
    if (open) {
      if (formId) {
        loadForm();
      } else {
        setName("");
        setTitle("");
        setDescription("");
        setSlug("");
        setFields([{ name: "", label: "", type: "text", required: false }]);
      }
    }
  }, [open, formId]);

  const loadForm = async () => {
    try {
      const { data } = await api.get(`/forms/${formId}`);
      setName(data.name || "");
      setTitle(data.title || "");
      setDescription(data.description || "");
      setSlug(data.slug || "");
      setFields(typeof data.fields === "string" ? JSON.parse(data.fields) : data.fields || []);
    } catch (err) {
      toast.error("Error al cargar formulario");
    }
  };

  const handleAddField = () => {
    setFields([...fields, { name: "", label: "", type: "text", required: false }]);
  };

  const handleRemoveField = (index) => {
    const newFields = [...fields];
    newFields.splice(index, 1);
    setFields(newFields);
  };

  const sanitizeVariableName = (val) => {
    if (!val) return "";
    return val
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  };

  const handleNameChange = (e) => {
    setName(sanitizeVariableName(e.target.value));
  };

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    if (!formId && !name) {
      setName(sanitizeVariableName(val));
    }
  };

  const handleFieldChange = (index, key, value) => {
    const newFields = [...fields];
    if (key === "name") {
      newFields[index][key] = sanitizeVariableName(value);
    } else {
      newFields[index][key] = value;
    }
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("El nombre técnico es requerido");
    
    // Validar fields
    const variableRegex = /^[a-z0-9_]+$/;
    if (!variableRegex.test(name.trim())) {
      return toast.error("El nombre técnico solo debe contener letras minúsculas, números y guiones bajos (_).");
    }
    const seenNames = new Set();

    for (let f of fields) {
      if (!f.name || !f.label) {
        return toast.error("Todos los campos deben tener Nombre(Key) y Etiqueta.");
      }
      if (!variableRegex.test(f.name)) {
        return toast.error(
          `La variable "${f.name}" no es válida. Solo debe contener letras minúsculas, números y guiones bajos (_) sin espacios ni caracteres especiales.`
        );
      }
      if (seenNames.has(f.name)) {
        return toast.error(
          `La variable "${f.name}" está duplicada. Cada campo debe tener un nombre de variable único.`
        );
      }
      seenNames.add(f.name);
    }

    const payload = {
      name: name.trim(),
      title: title.trim() || name.trim(),
      description: description.trim(),
      fields,
      slug // backend creates one if empty
    };

    try {
      if (formId) {
        await api.put(`/forms/${formId}`, payload);
        toast.success("Formulario actualizado");
      } else {
        await api.post("/forms", payload);
        toast.success("Formulario creado");
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error al guardar");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogHeader} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {formId ? i18n.t("globalModals.form.edit") : i18n.t("globalModals.form.new")}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent dividers dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Título del Formulario (Visible al Cliente)"
              fullWidth
              value={title}
              onChange={handleTitleChange}
              variant="outlined"
              margin="dense"
              placeholder="Ej: Registro de Compra"
              helperText="Encabezado que verá el cliente en la web"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre Técnico (Identificador Interno)"
              fullWidth
              value={name}
              onChange={handleNameChange}
              variant="outlined"
              margin="dense"
              placeholder="ej: registro_compra"
              helperText="Solo minúsculas, números y guiones bajos (_)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Slug / Enlace Web (opcional)"
              fullWidth
              value={slug}
              onChange={e => setSlug(e.target.value)}
              variant="outlined"
              margin="dense"
              helperText="Deja vacío para auto-generar (ej: registrodecompra-6ohdf2)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción / Instrucciones (Visible al Cliente)"
              fullWidth
              multiline
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              variant="outlined"
              margin="dense"
              placeholder="Ej: Por favor completa los siguientes campos para poder finalizar con el registro de tu compra."
              helperText="Aparecerá debajo del título para guiar al usuario"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginTop: 15, marginBottom: 10 }}>Campos del Formulario</Typography>
            {fields.map((field, index) => (
              <Grid container spacing={1} key={index} alignItems="center" style={{ marginBottom: 10 }}>
                <Grid item xs={3}>
                  <TextField
                    label="Variable / Key (ej: nombre)"
                    fullWidth
                    value={field.name}
                    onChange={e => handleFieldChange(index, "name", e.target.value)}
                    variant="outlined"
                    margin="dense"
                    helperText="Solo minúsculas y _"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Etiqueta (ej: Tu Nombre)"
                    fullWidth
                    value={field.label}
                    onChange={e => handleFieldChange(index, "label", e.target.value)}
                    variant="outlined"
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={field.type}
                      onChange={e => handleFieldChange(index, "type", e.target.value)}
                      label="Tipo"
                    >
                      <MenuItem value="text">Texto Corto</MenuItem>
                      <MenuItem value="textarea">Texto Largo</MenuItem>
                      <MenuItem value="number">Número</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="date">Fecha</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.required}
                        onChange={e => handleFieldChange(index, "required", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Req."
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" onClick={() => handleRemoveField(index)}>
                    <DeleteOutlineIcon color="secondary" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddField}
              variant="outlined"
              color="primary"
              style={{ marginTop: 10 }}
            >
              Añadir Campo
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} className={classes.secondaryButton}>
          Cancelar
        </Button>
        <Button onClick={handleSave} className={classes.applyButton}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormModal;
